import CustomFieldInput from '@/components/CustomFieldInput';
import type { SharedCustomField } from '@/types/serializers';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useMemo } from 'react';
import type { CustomFieldValue } from '@/utils/customFieldValidation';

type CustomFieldValues = Record<string, CustomFieldValue>;

type RegisterHiddenInput = (name: `customData.${number}`) => UseFormRegisterReturn;

type CustomFieldsFormProps = {
  fields: SharedCustomField[];
  values: CustomFieldValues;
  onChange: (fieldId: number, value: CustomFieldValue) => void;
  /** フォーカスアウト時にそのフィールドの検証をトリガーする */
  onBlur?: (fieldId: number) => void;
  registerHiddenInput?: RegisterHiddenInput;
  errors?: Record<string, string | undefined>;
  disabled?: boolean;
};

/**
 * カスタムフィールドを順番に表示する汎用フォーム
 */
export default function CustomFieldsForm({
  fields,
  values,
  onChange,
  onBlur,
  registerHiddenInput,
  errors,
  disabled,
}: CustomFieldsFormProps) {
  const sortedFields = useMemo(() => {
    return [...fields].sort((a, b) => a.order_number - b.order_number);
  }, [fields]);

  const nonLongTextFields = sortedFields.filter((field) => field.field_type !== 'long_text');
  const longTextFields = sortedFields.filter((field) => field.field_type === 'long_text');

  const pairedFields: SharedCustomField[][] = [];
  for (let i = 0; i < nonLongTextFields.length; i += 2) {
    const pair = [nonLongTextFields[i]];
    if (i + 1 < nonLongTextFields.length) {
      pair.push(nonLongTextFields[i + 1]);
    }
    pairedFields.push(pair);
  }

  return (
    <div className="mt-4">
      {pairedFields.map((pair, index) => (
        <div key={`pair-${index}`} className="mb-4 grid grid-cols-2 gap-4">
          {pair.map((field) => {
            const errorMessage = errors?.[String(field.id)];
            return (
              <div key={field.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm font-bold">
                  <span>{field.name}</span>
                  {field.is_required && (
                    <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                      必須
                    </span>
                  )}
                </div>
                {registerHiddenInput && (
                  <input type="hidden" {...registerHiddenInput(`customData.${field.id}`)} />
                )}
                <CustomFieldInput
                  field={field}
                  value={values[String(field.id)]}
                  onChange={(val) => onChange(field.id, val)}
                  onBlur={onBlur ? () => onBlur(field.id) : undefined}
                  error={!!errorMessage}
                  placeholder={`${field.name}を入力`}
                  className="w-full"
                  disabled={disabled}
                />
                {errorMessage && (
                  <p role="alert" className="mt-xxs text-body-xs text-danger">
                    {errorMessage}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {longTextFields.map((field) => {
        const errorMessage = errors?.[String(field.id)];
        return (
          <div key={field.id} className="mb-4 flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm font-bold">
              <span>{field.name}</span>
              {field.is_required && (
                <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                  必須
                </span>
              )}
            </div>
            {registerHiddenInput && (
              <input type="hidden" {...registerHiddenInput(`customData.${field.id}`)} />
            )}
            <CustomFieldInput
              field={field}
              value={values[String(field.id)]}
              onChange={(val) => onChange(field.id, val)}
              onBlur={onBlur ? () => onBlur(field.id) : undefined}
              error={!!errorMessage}
              placeholder={`${field.name}を入力`}
              className="w-full"
              disabled={disabled}
            />
            {errorMessage && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {errorMessage}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
